import * as React from 'react';
import { Input, Flex } from '@highereducation/hen-ui';
import type { QuestionProps } from '@de/components/Step/Step';
import type { Option, Step, Lead } from '@de/types/types';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import useViewEvents from '@de/hooks/useViewEvents';
import {
  createFieldEventsPayload,
  createStepElementViewedPayload,
} from '@de/utils/createEventingPayloadsDE';
import useFlowTrackingEvents from '@de/hooks/useFlowTrackingEvents';
import { isPii } from '@de/utils/isPii';
import { useFieldValidation } from '@de/hooks/useFieldValidation';
import { formatPhoneNumber } from '@de/utils/formatPhoneNumber';

/**
 * TextQuestion component using hen-ui design system.
 *
 * A text input question component built with hen-ui atomic components,
 * following EDU Design System standards. Handles various input types
 * including email, phone, and zip code with proper formatting and validation.
 *
 * @example
 * <TextQuestion
 *   id="firstName"
 *   leadFieldAssociated="firstName"
 *   placeholderText="Enter your first name"
 *   title="What's your first name?"
 *   stepName="Name"
 * />
 */
export const TextQuestion: React.FC<QuestionProps> = ({
  options = [],
  id = '',
  leadFieldAssociated,
  isMultiQuestionChild,
  placeholderText = 'Type your answer here...',
  title,
  stepName,
  variant,
}) => {
  const {
    updateFlowState,
    getStepMetadataById,
    validationErrors,
    clearValidationErrors,
    setValidationErrors,
    eventContext,
  } = useDynamicExperienceContext();
  const currentStepData = getStepMetadataById(id);
  const debounceTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const { triggerFieldEvents } = useFlowTrackingEvents();

  const isZipField = leadFieldAssociated === ('zip' as keyof Lead);
  const isEmailField = leadFieldAssociated === ('email' as keyof Lead);
  const isPhoneField = leadFieldAssociated === ('phoneNumber' as keyof Lead);
  const isNumericField = isZipField || isPhoneField;

  const currentError = validationErrors.find(
    (error) => error.field === leadFieldAssociated || error.field === id,
  );

  const hasValidationError = isMultiQuestionChild
    ? validationErrors.some(
        (error) => error.field === leadFieldAssociated || error.field === id,
      )
    : validationErrors.length > 0;

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = e.target.value;

      if (isPhoneField) {
        // Shared `(999) 999-9999` formatter — single source of truth with the
        // governed lead drawer's PII phone field (PiiFormSection).
        newValue = formatPhoneNumber(newValue);
      } else if (isNumericField) {
        newValue = newValue.replace(/\D/g, '');
      }

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }

      if (newValue.trim() === '') {
        updateFlowState(id, {});
        return;
      }

      const baseOption = options[0] || {
        optionId: 'text-input',
        group: 'text-input',
        text: 'Text Input',
      };

      const option: Option = {
        optionId: baseOption.optionId,
        text: baseOption.text || '',
        value: newValue,
        group: baseOption.group,
      };

      updateFlowState(id, { option, leadFieldAssociated });

      debounceTimeoutRef.current = setTimeout(() => {
        const debouncedOption: Option = {
          ...option,
          value: newValue,
        };

        updateFlowState(id, {
          option: debouncedOption,
          leadFieldAssociated,
          isDebounced: true,
        });

        triggerFieldEvents('FieldInputted', {
          ...eventContext,
          fieldValue: isPii(
            leadFieldAssociated?.toString() || '',
            eventContext?.currentStep as Step,
          )
            ? 'SANITIZED'
            : (debouncedOption.value as string),
          fieldNumber: eventContext?.currentStepIndex || 0,
          fieldName: isMultiQuestionChild
            ? stepName || ''
            : eventContext?.currentStep?.stepName || '',
          fieldType: 'text',
          fieldLabel: isMultiQuestionChild
            ? title
            : eventContext?.currentStep?.title,
          ...(isMultiQuestionChild && {
            stepId: id,
            stepName: stepName || title,
          }),
        });
      }, 2000);
    },
    [
      id,
      isMultiQuestionChild,
      title,
      options,
      leadFieldAssociated,
      updateFlowState,
      isNumericField,
      isPhoneField,
      eventContext,
      stepName,
      triggerFieldEvents,
    ],
  );

  const inputValue =
    ((currentStepData?.option as Option)?.value as string) || '';

  useFieldValidation(
    leadFieldAssociated?.toString(),
    inputValue,
    setValidationErrors,
    clearValidationErrors,
  );

  const inputType = isEmailField ? 'email' : 'text';
  const inputMode = isNumericField ? 'numeric' : undefined;
  const pattern = isNumericField ? '[0-9]*' : undefined;
  const maxLength = isZipField ? 5 : isPhoneField ? 14 : undefined;

  const leadFieldToAutoComplete: Partial<Record<keyof Lead, string>> = {
    firstName: 'given-name',
    lastName: 'family-name',
    email: 'email',
    phoneNumber: 'tel',
  };

  const autoComplete = leadFieldAssociated
    ? leadFieldToAutoComplete[leadFieldAssociated]
    : undefined;

  const fieldViewedContext = isMultiQuestionChild
    ? {
        ...eventContext,
        fieldName: stepName || leadFieldAssociated?.toString() || '',
        fieldType: 'text',
        stepId: id,
        stepName: stepName || title,
      }
    : eventContext;

  const { ref: visibilityRef } = useViewEvents([
    createStepElementViewedPayload({
      ...eventContext,
      variant: variant || 'text',
    }),
    createFieldEventsPayload(fieldViewedContext, 'FieldViewed'),
  ]);

  return (
    <Flex
      className='w-full h-full py-xxs'
      ref={visibilityRef as React.LegacyRef<HTMLDivElement>}
    >
      <Input
        type={inputType}
        inputMode={inputMode}
        pattern={pattern}
        value={inputValue}
        onChange={handleInputChange}
        placeholder={placeholderText}
        maxLength={maxLength}
        autoComplete={autoComplete}
        name={leadFieldAssociated?.toString() || id}
        id={leadFieldAssociated?.toString() || id}
        error={hasValidationError && !!currentError?.message}
        errorMessage={currentError?.message}
      />
    </Flex>
  );
};

export default TextQuestion;
